import type { ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Editor from "@/v2-backoffice-client/components/editor"
import FeatureHotfixNotice from "@/v2-backoffice-client/feature/FeatureHotfixNotice"
import { registerCatalogEditorSchemas } from "@/v2-backoffice-client/feature/configuration/feature.configuration.schema"

export type FeatureConfigurationEditorProps = {
  className?: string
  /** Monaco model URI; binds this editor to its registered JSON schema. */
  modelPath: string
  /** The saved (or seeded) blob, pretty-printed. */
  value: string
  isSaving?: boolean
  /** Receives the parsed draft — the editor validates the JSON first. */
  onSave(value: unknown): void
  /** Observes the raw draft text; the create flows mirror it into their payload. */
  onDraftChange?(text: string): void
  /** Renamed by the create flows to "Create basemap"/"Create nav layer". */
  saveLabel?: ReactNode
  /** Keeps save enabled with an unchanged document (create flows). */
  canSaveClean?: boolean
}

/**
 * The one editing surface for a feature that is configured by a single JSON
 * document — a basemap or a nav layer. Same schema-assisted editor and the
 * same hotfix warning as the layer workbench, without the parts only a map
 * layer has (five documents, a live preview, drift, the AI assistant).
 *
 * Stateless by design: the host owns the value and the save, so the sheet
 * and the create flow can each bind it to their own mutation.
 */
function FeatureConfigurationEditor(props: FeatureConfigurationEditorProps) {
  const {
    modelPath,
    value,
    isSaving,
    onSave,
    onDraftChange,
    saveLabel,
    canSaveClean,
    className,
  } = props

  return (
    <div
      className={cn(
        "feature-configuration-editor flex min-h-[420px] flex-1 flex-col gap-2 pt-2",
        className,
      )}
    >
      <FeatureHotfixNotice />
      <Editor
        className="min-h-0 flex-1"
        value={value}
        path={modelPath}
        beforeMount={registerCatalogEditorSchemas}
        isSaving={isSaving}
        saveLabel={saveLabel}
        canSaveClean={canSaveClean}
        onChange={onDraftChange}
        onSave={(text) => onSave(JSON.parse(text))}
      />
    </div>
  )
}

export default FeatureConfigurationEditor
